import { useRef, type KeyboardEvent, type ReactNode } from 'react';

/**
 * Kelompok pilihan tunggal dengan semantik radio dan navigasi panah.
 *
 * Sebelumnya pemilih skala dibuat dari tombol ber-`aria-pressed`. Itu terbaca
 * pembaca layar sebagai empat sakelar terpisah — bukan "pilihan 2 dari 4" —
 * dan pengguna keyboard harus menekan Tab empat kali untuk melewati SATU
 * indikator; pada instrumen 16 indikator itu 64 ketukan hanya untuk menggulir.
 *
 * Pola yang benar adalah roving tabindex: seluruh kelompok menempati satu
 * perhentian Tab, lalu panah memindahkan pilihan di dalamnya.
 */

interface RadioGroupProps<T> {
  label: string;
  value: T | null;
  options: T[];
  disabled?: boolean;
  /** Mengetuk pilihan yang sama membatalkannya — satu-satunya cara mengosongkan. */
  onChange: (value: T | null) => void;
  getKey: (option: T) => string | number;
  className?: string;
  children: (option: T, state: { selected: boolean }) => ReactNode;
  /**
   * Kelas untuk tombol pilihannya sendiri.
   *
   * Sengaja tidak memakai `display: contents` pada tombol supaya pembungkus
   * ini tak terlihat: di beberapa peramban itu mengeluarkan tombol dari pohon
   * aksesibilitas, dan cincin fokusnya ikut hilang.
   */
  optionClassName: (option: T, state: { selected: boolean }) => string;
  /** Nama pilihan untuk pembaca layar, bila isi visualnya berupa angka saja. */
  getLabel?: (option: T) => string;
}

export function RadioGroup<T>({
  label,
  value,
  options,
  disabled,
  onChange,
  getKey,
  className,
  children,
  optionClassName,
  getLabel,
}: RadioGroupProps<T>) {
  const ref = useRef<HTMLDivElement>(null);

  const selectedIndex = options.findIndex((o) => value !== null && getKey(o) === getKey(value));

  // Saat belum ada yang dipilih, pilihan PERTAMA yang menerima fokus. Kalau
  // semuanya tabindex -1, kelompok ini hilang sama sekali dari urutan Tab.
  const focusableIndex = selectedIndex === -1 ? 0 : selectedIndex;

  const move = (delta: number) => {
    if (disabled || options.length === 0) return;
    const next = (focusableIndex + delta + options.length) % options.length;
    const target = options[next];
    if (target === undefined) return;
    onChange(target);
    // Fokus mengikuti pilihan, sesuai pola radio bawaan peramban.
    const nodes = ref.current?.querySelectorAll<HTMLElement>('[role="radio"]');
    nodes?.[next]?.focus();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault();
        move(1);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault();
        move(-1);
        break;
      default:
    }
  };

  return (
    <div ref={ref} role="radiogroup" aria-label={label} onKeyDown={onKeyDown} className={className}>
      {options.map((option, i) => {
        const selected = selectedIndex === i;
        return (
          <button
            key={getKey(option)}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={getLabel?.(option)}
            disabled={disabled}
            tabIndex={i === focusableIndex ? 0 : -1}
            onClick={() => onChange(selected ? null : option)}
            className={optionClassName(option, { selected })}
          >
            {children(option, { selected })}
          </button>
        );
      })}
    </div>
  );
}
